/**
 * DimensionsManager — Core Layer state container for vehicle dimension overlay.
 *
 * Pure state + geometry helpers. No React, no R3F.
 * The `DimensionsOverlay` R3F component reads state from this manager.
 *
 * Responsibilities:
 * - Compute auto-dimensions from AABB bounding box
 * - Accept manual dimension overrides (mm → m conversion)
 * - Toggle visibility on/off
 * - Enable/disable the feature (controls whether clicks are handled)
 * - Provide resolved values in metres for display
 * - Track AABB min/max for line positioning in the overlay
 *
 * @see docs/ims-engine-api/12-feature-pano-measures.md
 * @see docs/ims-engine-api/02-api-contract.md
 */

import type {
  DimensionsMode,
  DimensionsValues,
  DimensionsManualValues,
  DimensionsConfig,
  DimensionsState,
  DimensionsAABB,
  DimensionsToggledEvent,
} from '../api/types/dimensions';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Millimetres to metres conversion factor. */
const MM_TO_M = 0.001;

// ---------------------------------------------------------------------------
// DimensionsManager
// ---------------------------------------------------------------------------

export class DimensionsManager {
  /** Whether dimension lines are currently visible. */
  private _visible = false;
  /** Whether the feature is enabled (user can toggle). */
  private _enabled = false;
  /** Active dimension mode. */
  private _mode: DimensionsMode = 'auto';
  /** AABB-derived auto values in metres. */
  private _autoValues: DimensionsValues = { length: 0, width: 0, height: 0 };
  /** Manual override values in mm (null when in auto mode). */
  private _manualValues: DimensionsManualValues | null = null;
  /** Raw AABB for line positioning in the overlay. */
  private _aabb: DimensionsAABB | null = null;

  /** Called when visibility toggles. */
  onToggled?: (event: DimensionsToggledEvent) => void;
  /** Called on any state change (for DimensionsOverlay subscription). */
  onChange?: () => void;

  // -----------------------------------------------------------------------
  // init — called once from createEngine's onMounted callback
  // -----------------------------------------------------------------------

  /**
   * Initialise with AABB from the loaded model and optional config.
   *
   * @param aabb - Bounding box of the cloned scene (from Box3.setFromObject)
   * @param config - Optional dimensions config (manual values, mode)
   */
  init(aabb: DimensionsAABB, config?: DimensionsConfig): void {
    this._aabb = aabb;

    // Compute auto values from AABB (in metres — Three.js units)
    // Standard Three.js/GLTF convention: Z-axis = front-to-back (length),
    // X-axis = side-to-side (width), Y-axis = vertical (height).
    this._autoValues = {
      length: Math.abs(aabb.max.z - aabb.min.z),
      width: Math.abs(aabb.max.x - aabb.min.x),
      height: Math.abs(aabb.max.y - aabb.min.y),
    };

    // Apply config if provided
    if (config) {
      this._applyConfig(config);
    }

    this.onChange?.();
  }

  // -----------------------------------------------------------------------
  // enable / disable
  // -----------------------------------------------------------------------

  /** Enable the dimensions feature (allows toggling). */
  enable(): void {
    this._enabled = true;
    this.onChange?.();
  }

  /** Disable the feature and hide overlay. */
  disable(): void {
    this._enabled = false;
    if (this._visible) {
      this._visible = false;
      this.onToggled?.({ visible: false });
    }
    this.onChange?.();
  }

  // -----------------------------------------------------------------------
  // toggle — called by model click or API
  // -----------------------------------------------------------------------

  /**
   * Toggle dimension overlay visibility.
   * If `force` is provided, sets visibility to that value instead.
   * Only works when the feature is enabled.
   */
  toggle(force?: boolean): void {
    if (!this._enabled) return;

    const next = force !== undefined ? force : !this._visible;
    if (next === this._visible) return;

    this._visible = next;
    this.onToggled?.({ visible: next });
    this.onChange?.();
  }

  // -----------------------------------------------------------------------
  // setDimensions — runtime config update
  // -----------------------------------------------------------------------

  /**
   * Update dimensions config at runtime.
   * Allows switching mode and/or setting manual values.
   */
  setDimensions(config: DimensionsConfig): void {
    this._applyConfig(config);
    this.onChange?.();
  }

  // -----------------------------------------------------------------------
  // getState
  // -----------------------------------------------------------------------

  /** Get the full dimensions state snapshot. */
  getState(): DimensionsState {
    return {
      visible: this._visible,
      enabled: this._enabled,
      mode: this._mode,
      values: this._getResolvedValues(),
      autoValues: { ...this._autoValues },
      manualValues: this._manualValues ? { ...this._manualValues } : null,
    };
  }

  /** Get the AABB for overlay positioning. Returns null if not initialised. */
  getAABB(): DimensionsAABB | null {
    return this._aabb
      ? { ...this._aabb, min: { ...this._aabb.min }, max: { ...this._aabb.max } }
      : null;
  }

  // -----------------------------------------------------------------------
  // dispose
  // -----------------------------------------------------------------------

  dispose(): void {
    this._visible = false;
    this._enabled = false;
    this._aabb = null;
    this._manualValues = null;
    this._autoValues = { length: 0, width: 0, height: 0 };
    this.onToggled = undefined;
    this.onChange = undefined;
  }

  // -----------------------------------------------------------------------
  // Private
  // -----------------------------------------------------------------------

  private _applyConfig(config: DimensionsConfig): void {
    // Set manual values if any dimension is provided
    const hasManual =
      config.lengthMm !== undefined ||
      config.widthMm !== undefined ||
      config.heightMm !== undefined;

    if (hasManual) {
      this._manualValues = {
        lengthMm:
          config.lengthMm ?? this._manualValues?.lengthMm ?? this._autoValues.length / MM_TO_M,
        widthMm: config.widthMm ?? this._manualValues?.widthMm ?? this._autoValues.width / MM_TO_M,
        heightMm:
          config.heightMm ?? this._manualValues?.heightMm ?? this._autoValues.height / MM_TO_M,
      };
    }

    // Set mode — explicit config takes priority, else infer from values
    if (config.mode) {
      this._mode = config.mode;
    } else if (hasManual) {
      this._mode = 'manual';
    }

    // If mode is 'auto', clear manual values
    if (this._mode === 'auto') {
      this._manualValues = null;
    }
  }

  /** Get the resolved display values in metres based on current mode. */
  private _getResolvedValues(): DimensionsValues {
    if (this._mode === 'manual' && this._manualValues) {
      return {
        length: this._manualValues.lengthMm * MM_TO_M,
        width: this._manualValues.widthMm * MM_TO_M,
        height: this._manualValues.heightMm * MM_TO_M,
      };
    }
    return { ...this._autoValues };
  }
}
